import { Page, Header, Content, Link, Progress } from '@backstage/core-components';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { Box, Card, Grid, IconButton, Typography } from '@material-ui/core';
import { Code, LibraryBooks, ChevronRight, Web, EmojiObjects, Assessment, Widgets } from '@material-ui/icons'; // ícones do MUI
import { useStyles } from './style';
import { HomePageRecentlyVisited, HomePageStarredEntities, HomePageTopVisited } from '@backstage/plugin-home';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useEffect } from 'react';

type DashCardProps = {
  title: string;
  description: string | React.ReactNode;
  icon: React.ReactNode;
  href: string;
  color: string;
  bgColor: string;
}

const DashCard = ({ title, description, bgColor, color, icon, href }: DashCardProps) => {
  const classes = useStyles();

  return <Link to={href} style={{ textDecoration: "none", color: "inherit" }}>
    <Card className={classes.card} style={{ background: bgColor }}>
      <div style={{ gap: 8, display: 'flex', flexDirection: 'column' }}>
        <div className={classes.cardContent}>
          <div className={classes.iconContainer} style={{ background: color }}>
            {icon}
          </div>
          <div style={{ color: '#1351B4' }} className={classes.contentText}>
            <span className={classes.cardTitle}>{title}</span>
          </div>
          <IconButton size='small' style={{ background: '#FFF' }}>
            <ChevronRight />
          </IconButton>
        </div>
        <div style={{ padding: 16, paddingTop: 0 }}>
          <span className={classes.cardDescription}>{description}</span>
        </div>
      </div>
    </Card>
  </Link>
}

export const HomePage = () => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const showcaseOlimpiaUrl = config.getOptionalString("app.showcaseOlimpiaUrl") || "";
  const dashboardMonitorUrl = config.getOptionalString("app.dashboardMonitorUrl") || "";
  const apiOlimpiaUrl = config.getOptionalString("app.apiOlimpiaUrl") || '/api-docs';

  return (
    <Page themeId="home">
      <Header
        title="Página Principal"
      />
      <Content noPadding>
        <Box className={classes.container}>
          <div className={classes.boxWelcome}>
            <div className={classes.welcomeContainer}>
              <Typography variant='body2' style={{ color: '#FFF' }}>
                <Typography style={{ fontSize: 24 }}>Seja bem-vindo ao <span className={classes.textHighlight}>Portal OLÍMPIA</span>, <br />
                  o ambiente corporativo da Dataprev dedicado à <span className={classes.textHighlight}>Inteligência Artificial (IA).</span></Typography>
                <br />Aqui você encontra, em um só lugar, ferramentas, serviços, boas práticas e orientações para apoiar o uso estratégico da IA na empresa.
                <br /><br />
                Nosso objetivo é <b>facilitar o acesso, promover o uso responsável e acelerar a adoção governada da IA</b> nos produtos e soluções desenvolvidos pela Dataprev.
                <br />Tudo isso com foco em inovação, eficiência e segurança.
              </Typography>
              <div className={classes.searchbar}>
                <SidebarSearchModal />
              </div>
            </div>
          </div>
          <div className={classes.boxCard}>
            <div className={classes.cardContainer}>
              <DashCard
                href='/catalog'
                bgColor='rgba(145, 4, 110, 0.1)'
                color='#91046e'
                title='Catálogo'
                description={
                  <div>
                    <b>Soluções a um clique de distância</b> <br /><br />
                    Liste as aplicações e os serviços existentes. Explore componentes e construa produtos eficazes e com qualidade garantida.
                  </div>
                }
                icon={<Widgets htmlColor='#fff' />}
              />
              <DashCard
                href={apiOlimpiaUrl}
                bgColor='rgba(79, 205, 68, 0.075)'
                color='#4fcd44'
                title='Explore nossas APIs'
                description={
                  <div>
                    <b>Conecte-se à inovação com segurança e agilidade</b> <br /><br />
                    Acesse APIs inteligentes e prontas para integração. Crie soluções escaláveis com rapidez, mantendo os padrões de segurança e governança da Dataprev.
                  </div>
                }
                icon={<Code htmlColor='#fff' />}
              />
              <DashCard
                href='/docs/default/resource/techdocs-docs'
                bgColor='rgba(54, 179, 246, 0.1)'
                color='#36b3f6'
                title='Documentação Técnica'
                description={
                  <div>
                    <b>Seu guia completo para desenvolver com confiança</b> <br /><br />
                    Tenha acesso a tutoriais, guias técnicos e boas práticas que vão te ajudar a construir soluções robustas e eficientes com IA na Dataprev.
                  </div>
                }
                icon={<LibraryBooks htmlColor='#fff' />}
              />
              <DashCard
                href='/create'
                bgColor='rgba(41, 199, 209, 0.1)'
                color='#29c7d1'
                title='Templates'
                description={
                  <div>
                    <b>Agilidade e padronização ao seu alcance</b><br /><br />
                    Encontre modelos prontos para acelerar seus projetos de IA. Economize tempo com templates organizados e alinhados às melhores práticas da empresa.
                  </div>
                }
                icon={<Web htmlColor='#fff' />}
              />
              <DashCard
                href={showcaseOlimpiaUrl}
                bgColor='rgba(255, 178, 0, 0.07)'
                color='#ffb200'
                title='Showcase IA'
                description={
                  <div>
                    <b>Explore o poder da Inteligência Artificial na prática</b> <br /><br />
                    Conheça as capacidades de IA já disponíveis na Plataforma OLIMPIA. Experimente serviços e aplicações que estão transformando a forma como a Dataprev entrega valor à sociedade.
                  </div>
                }
                icon={<EmojiObjects htmlColor='#fff' />}
              />
              <DashCard
                href={dashboardMonitorUrl}
                bgColor='rgba(231, 64, 23, 0.07)'
                color='#e74017'
                title='Monitoramento'
                description={
                  <div>
                    <b>Explore métricas e estatísticas</b> <br /><br />
                    Monitore as aplicações, extraia insights e evolua as capacidades de seus Serviços e Aplicações.
                  </div>
                }
                icon={<Assessment htmlColor='#fff' />}
              />
            </div>

            <Grid container spacing={3}>
              <Grid item xs={6}>
                <HomePageStarredEntities noStarredEntitiesMessage="Você ainda não adicionou nenhum favorito" title='Favoritos' />
              </Grid>
              <Grid item xs={6}>
                <HomePageTopVisited title='Mais Visitados' kind='top' />
              </Grid>
              <Grid item xs={6}>
                <HomePageRecentlyVisited title='Visitados Recentemente' kind='top' />
              </Grid>
            </Grid>
          </div>
        </Box>
      </Content>
    </Page>
  );
};
